// ============================================================================
// view.webformhub — authenticated /dailys lookup ownership, complete
// ============================================================================
//
// The app-side WebformHub bridge owns everything the six daily forms render
// from: the presenter's config bundle, its feed-input lookup, the two layer
// directories, and now the two app_store documents that were the last values
// this surface took off App's global boot. All of it through scoped TanStack
// Query resources in the surface's own authenticated scope.
//
// WHY THE LIST IS EMPTY, RESIDUAL BY RESIDUAL.
//
//   legacy.context.layer.batches / .layer.housings
//     Gone one wave ago: webform.hub.layerDirectory reads both tables here, so
//     the entry no longer hands either array down. The matching retirements are
//     declared in tests/fixtures/former-eager-dependencies.json.
//
//   legacy.context.layer.groups
//     The ppp-layer-groups-v1 app_store blob, not a table read — the /dailys egg
//     form seeds its four group NAMES from the first four active rows. It is
//     read through layer.eggDailys.groups now, the resource that already owns
//     that exact key for the egg index, unfiltered and in blob order. The four
//     values are not cosmetic: they are written to egg_dailys.group1_name..
//     group4_name on every submitted report, and the fourth of them lands on
//     about one production row a day. They are pinned across the move, case by
//     case, by tests/static/webform_hub_egg_group_defaults_static.test.js
//     against a baseline generated from the ORIGINAL code before it changed.
//
//   legacy.context.webformsConfig.webformsConfig
//     The ppp-webforms-v1 admin document, read through webform.pigDailys.config
//     — the same resource, the same key and the same applyWebformsConfigSeeds
//     merge the direct Pig Dailys form already uses, with the same fail-soft
//     resolution to DEFAULT_WEBFORMS_CONFIG that WebformsConfigContext held
//     before App's boot replaced it. It stays the SECOND-choice document: the
//     webform_config mirror webform.hub.config reads wins whenever it resolves.
//
//   legacy.context.webformsConfig.wfGroups
//     Deleted rather than replaced, because it was a closed loop. Its only
//     writer was this surface's own bridge, mirroring the active pig-group list
//     it had just read; its only reader was the hub's fallback for that same
//     value, which within one mount was unreachable by construction. The one
//     behaviour it really had was a cross-mount carry-over — a second visit to
//     /dailys whose lookup failed showed the previous visit's list — and that
//     is declared as lost, not hidden. A failed lookup now shows an empty pig
//     group list, exactly as the bridge's other pending or rejected reads do.
//
//   legacy.boot.dataLoaded
//     Drops with them. It records a surface whose CONTENT depends on the global
//     boot; nothing this page renders comes from that transaction now. The
//     surface still sits BEHIND App's gate, as every unreleased surface does —
//     that gate is the thing a later boot release removes, not a residual.
//
// WHAT DID NOT MOVE, AND IS NOT HIDDEN HERE.
//
// The one thing the change alters besides ownership is WHEN the two documents
// arrive. App's gate guaranteed both were resolved before the presenter's first
// render; a scoped query is not. Every value the hub takes from them is read at
// render time and self-corrects — except the egg form's four default group
// names, which useState reads once, so the presenter applies those on arrival
// instead, at most once and only over an untouched form.
//
// THIS RELEASES THE CLOSED HUB WHITELIST, NOT THE WHOLE SPLAT. The corresponding
// ledger row admits only the seven raw pathnames in webformHubDailysPath.ts.
// `/dailys/tasks` remains an exact, separately owned Tasks route with its own
// released mount; every other suffix stays on the existing legacy path until a
// later review widens the whitelist with evidence.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.webformhub',
  obligations: Object.freeze([
    Object.freeze({id: 'webform.hub.inputs', owner: 'permanent'}),
    Object.freeze({id: 'webform.hub.config', owner: 'permanent'}),
    Object.freeze({id: 'webform.hub.layerDirectory', owner: 'permanent'}),
    Object.freeze({id: 'layer.eggDailys.groups', owner: 'permanent'}),
    Object.freeze({id: 'webform.pigDailys.config', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
